import { Suspense, lazy } from "react";
import { Routes, Route, BrowserRouter, Outlet } from "react-router-dom";
import ProtectedRoute from "./layout/ProtectRoute/ProtectedRoute";
import MainLayout from "./layout/MainLayout";
import useShopAndProductInfo, { ShopAndProductProvider } from "./hooks/useShopAndProductInfo";
import PortalShell from "./components/Portal/PortalShell";
import PageSpinner from "./components/PageSpinner/PageSpinner";
const Home = lazy(() => import("./Pages/Order"));
const Login = lazy(() => import("./Pages/Login"));
const Guest = lazy(() => import("./Pages/Guest"));
const GuestStoreReview = lazy(() => import("./Pages/Guest/GuestStoreReview"));
const WriteReviewProduct = lazy(() => import("./Pages/WriteReviewProduct"));
const WriteReviewForm = lazy(() => import("./Pages/WriteReviewForm"));
const DetailPage = lazy(() => import("./Pages/ReviewDetail"));
const NotFound = lazy(() => import("./Pages/NotFound"));

// Every page renders inside one of two layout routes, which both use PortalShell
// (header + scrollable content); pages put their content in PortalBody
// (column + "Powered by" footer):
//  - GuestLayout: guest QR flow, shop from the guest QR settings
//  - MainLayout: login QR flow (login + logged-in pages), shop from the login QR settings

// Shared provider keeps shop/products data fetched once across the guest flow
// (list <-> write review) instead of refetching on every navigation.
const GuestShell = () => {
  const { shopInfo, globalDisplay } = useShopAndProductInfo();
  return (
    <PortalShell shop={shopInfo} globalDisplay={globalDisplay}>
      {/* Inner Suspense so lazy-loading a child page doesn't unmount the
          provider (which would refetch shop/products on every navigation). */}
      <Suspense fallback={<PageSpinner />}>
        <Outlet />
      </Suspense>
    </PortalShell>
  );
};

const GuestLayout = () => (
  <ShopAndProductProvider>
    <GuestShell />
  </ShopAndProductProvider>
);

const Routers = () => {
  return (
    <Suspense fallback={<PageSpinner fullScreen />}>
      <BrowserRouter>
        <Routes>
          <Route path="/404" element={<NotFound />} />
          <Route element={<GuestLayout />}>
            <Route path="/:shop_id/guest" element={<Guest />} />
            <Route path="/:shop_id/guest/store-review" element={<GuestStoreReview />} />
            <Route path="/:shop_id/guest/:product_id" element={<WriteReviewProduct />} />
            <Route path="/:shop_id/write-review/form/:product_id" element={<WriteReviewForm />} />
          </Route>
          <Route element={<MainLayout />}>
            <Route path="/:shop_id/login" element={<Login />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/:shop_id/*" element={<Home />} />
              <Route path="/:shop_id/detail/:order_id/:product_id/:review_id/*" element={<DetailPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

export default Routers;
